import { Filter, X } from "lucide-react";
import type React from "react";
import { useMemo, useState } from "react";
import type { VisualEdge, VisualNode } from "@/types/knowledgeGraph";
import { COMMUNITY_COLORS } from "@/utils/graphAnalysis";

export interface FilterCriteria {
	itemType: "node" | "edge" | null;
	property: string | null;
	value: string | null;
}

interface FilterPanelProps {
	nodes: VisualNode[];
	edges: VisualEdge[];
	onFilter: (filter: FilterCriteria) => void;
	onReset: () => void;
	visible: boolean;
	onToggle: () => void;
}

/**
 * Filter Panel component
 * Provides filtering controls for nodes and edges by properties
 */
export const FilterPanel: React.FC<FilterPanelProps> = ({ nodes, edges, onFilter, onReset, visible, onToggle }) => {
	const safeNodes = nodes ?? [];
	const safeEdges = edges ?? [];
	const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>({
		itemType: null,
		property: null,
		value: null,
	});

	const [selectedNodeId, setSelectedNodeId] = useState<string>("");

	// Get available properties based on selected item type
	const availableProperties = useMemo(() => {
		if (filterCriteria.itemType === "node") {
			return ["id", "label", "color", "elementType", "community"];
		} else if (filterCriteria.itemType === "edge") {
			return ["from", "to", "label", "inferred"];
		}
		return [];
	}, [filterCriteria.itemType]);

	// Get available values for selected property
	const availableValues = useMemo(() => {
		if (!filterCriteria.itemType || !filterCriteria.property) return [];

		const values = new Set<string>();

		if (filterCriteria.itemType === "node") {
			safeNodes.forEach((node) => {
				if (filterCriteria.property === "community") {
					// Handle community specially - convert color to community ID
					const colorIndex = COMMUNITY_COLORS.indexOf(node.color || "");
					if (colorIndex >= 0) {
						values.add(colorIndex.toString());
					}
				} else if (filterCriteria.property === "id") {
					values.add(node.id);
				} else if (filterCriteria.property === "label") {
					values.add(node.name);
				} else if (filterCriteria.property === "color") {
					if (node.color) values.add(node.color);
				} else if (filterCriteria.property === "elementType") {
					values.add(node.elementType);
				}
			});
		} else if (filterCriteria.itemType === "edge") {
			safeEdges.forEach((edge) => {
				if (filterCriteria.property === "inferred") {
					values.add((edge.inferred || false).toString());
				} else if (filterCriteria.property === "from") {
					const source = typeof edge.source === "string" ? edge.source : edge.source.id;
					values.add(source);
				} else if (filterCriteria.property === "to") {
					const target = typeof edge.target === "string" ? edge.target : edge.target.id;
					values.add(target);
				} else if (filterCriteria.property === "label") {
					values.add(edge.label);
				}
			});
		}

		return Array.from(values).sort();
	}, [filterCriteria.itemType, filterCriteria.property, safeNodes, safeEdges]);

	// Get node IDs for node selector
	const nodeIds = useMemo(() => {
		return safeNodes.map((node) => node.id).sort();
	}, [safeNodes]);

	const handleItemTypeChange = (itemType: "node" | "edge" | null) => {
		setFilterCriteria({
			itemType,
			property: null,
			value: null,
		});
	};

	const handlePropertyChange = (property: string) => {
		setFilterCriteria({
			...filterCriteria,
			property,
			value: null,
		});
	};

	const handleValueChange = (value: string) => {
		setFilterCriteria({
			...filterCriteria,
			value,
		});
	};

	const handleApplyFilter = () => {
		if (filterCriteria.itemType && filterCriteria.property && filterCriteria.value) {
			onFilter(filterCriteria);
		}
	};

	const handleNodeSelect = (nodeId: string) => {
		setSelectedNodeId(nodeId);
		if (nodeId) {
			// Filter to show only this node and its connections
			onFilter({
				itemType: "node",
				property: "id",
				value: nodeId,
			});
		} else {
			onReset();
		}
	};

	const handleReset = () => {
		setFilterCriteria({
			itemType: null,
			property: null,
			value: null,
		});
		setSelectedNodeId("");
		onReset();
	};

	if (!visible) {
		return (
			<button
				type="button"
				onClick={onToggle}
				className="surface border border-subtle rounded-lg shadow-sm p-3 mb-4 soft-hover transition-colors flex items-center gap-2"
			>
				<Filter className="w-4 h-4 text-muted" />
				<span className="text-sm font-medium text-muted">Show Filters</span>
			</button>
		);
	}

	return (
		<div className="surface border border-subtle rounded-lg shadow-sm p-4 mb-4">
			<div className="flex items-center justify-between mb-4">
				<div className="flex items-center gap-2">
					<Filter className="w-5 h-5 text-muted" />
					<h3 className="text-sm font-semibold text-primary">Filters</h3>
				</div>
				<button type="button" onClick={onToggle} className="text-sm text-subtle hover-text transition-colors">
					<X className="w-4 h-4" />
				</button>
			</div>

			{/* Node Selection */}
			<div className="mb-4 p-3 rounded-md surface-muted border border-strong">
				<label htmlFor="node-select" className="block text-xs font-medium text-muted mb-2">
					Select Node by ID
				</label>
				<select
					id="node-select"
					value={selectedNodeId}
					onChange={(e) => handleNodeSelect(e.target.value)}
					className="w-full px-3 py-2 text-sm border border-strong surface text-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
				>
					<option value="">Select a Node</option>
					{nodeIds.map((nodeId) => (
						<option key={nodeId} value={nodeId}>
							{nodeId}
						</option>
					))}
				</select>
			</div>

			{/* Property-based Filtering */}
			<div className="space-y-3 p-3 rounded-md surface-muted border border-strong">
				<div>
					<label htmlFor="item-type-select" className="block text-xs font-medium text-muted mb-2">
						Item Type
					</label>
					<select
						id="item-type-select"
						value={filterCriteria.itemType || ""}
						onChange={(e) => handleItemTypeChange(e.target.value as "node" | "edge" | null)}
						className="w-full px-3 py-2 text-sm border border-strong surface text-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
					>
						<option value="">Select item type</option>
						<option value="node">Nodes</option>
						<option value="edge">Edges</option>
					</select>
				</div>

				{filterCriteria.itemType && (
					<div>
						<label htmlFor="property-select" className="block text-xs font-medium text-muted mb-2">
							Property
						</label>
						<select
							id="property-select"
							value={filterCriteria.property || ""}
							onChange={(e) => handlePropertyChange(e.target.value)}
							className="w-full px-3 py-2 text-sm border border-strong surface text-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
						>
							<option value="">Select a property</option>
							{availableProperties.map((prop) => (
								<option key={prop} value={prop}>
									{prop}
								</option>
							))}
						</select>
					</div>
				)}

				{filterCriteria.property && (
					<div>
						<label htmlFor="value-select" className="block text-xs font-medium text-muted mb-2">
							Value
						</label>
						<select
							id="value-select"
							value={filterCriteria.value || ""}
							onChange={(e) => handleValueChange(e.target.value)}
							className="w-full px-3 py-2 text-sm border border-strong surface text-primary rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
						>
							<option value="">Select value(s)</option>
							{availableValues.map((value) => (
								<option key={value} value={value}>
									{value}
								</option>
							))}
						</select>
					</div>
				)}

				<div className="flex gap-2 pt-2">
					<button
						type="button"
						onClick={handleApplyFilter}
						disabled={!filterCriteria.itemType || !filterCriteria.property || !filterCriteria.value}
						className="flex-1 px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
					>
						Apply Filter
					</button>
					<button
						type="button"
						onClick={handleReset}
						className="px-4 py-2 text-sm font-medium text-muted surface border border-strong rounded-md soft-hover focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
					>
						Reset
					</button>
				</div>
			</div>
		</div>
	);
};

export default FilterPanel;
